import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Box } from '@mui/material';
import { BodyText } from '../../typography/PrimaryTypography';
import { Scope } from '@esg/esg-global-types';
import { getScopes } from '../../../../graphql/services/scope.service';
import { sortScopes } from '../../../../lib/app/scope';

interface ScopeSelectProps {
  handleChangeSelection: (selected_options: Array<Scope> | Scope | null) => void;
  options?: Array<Scope>;
  selected_options?: Array<Scope> | Scope | null;
  disabled?: boolean;
  input_label?: string;
  clearable?: boolean;
  multi_select?: boolean;
  tag_limit?: number;
  searchable?: boolean;
  color?: string;
  disable_underline?: boolean;
  input_icon?: React.ReactNode;
  disable_selected_tags?: boolean;
  scope_prefix?: string;
}

/**
 * Scope selection auto-complete component that handles a variety of built-in scenarios.
 * @param {(selected_options: Array<string> | string | null): void} props.handleChangeSelection Notify the parent component when the selected options have changed.
 * @param {Array<string>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {string | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {string} props.color A custom color used for the input component of the autocomplete. Defaults to standard MUI color.
 * @param {string} props.disable_underline Allows the hiding of the underline on a standard variant text field.
 * @param {ReactNode} props.input_icon Optionally provide an icon to display next to the input label.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @param {string} props.scope_prefix Optionally filter the scopes by the input value.
 * @returns {JSX.Element}
 */
const ScopeSelect = (props: ScopeSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<Scope>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<Array<Scope> | Scope | null>(
    props.multi_select ? [] : null
  );
  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<Scope> = props.options
        ? // Options supplied by the parent component.
          props.options
        : await getScopes({ scope_prefix: props.scope_prefix });
      sortScopes(options);
      setOptions(options);
    })();
  }, [props.options, props.scope_prefix]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  return (
    <Autocomplete
      id="scope-select"
      fullWidth
      multiple={props.multi_select}
      limitTags={props.tag_limit}
      disableCloseOnSelect={props.multi_select}
      disabled={props.disabled}
      disableClearable={props.clearable === false}
      options={optionList}
      value={selectedOptions}
      getOptionLabel={(option: Scope) => {
        return `${option.scope}: ${option.description}`;
      }}
      isOptionEqualToValue={(option: Scope, value: Scope) => option.scope === value.scope}
      noOptionsText={`No Options`}
      renderValue={
        !props.multi_select || !props.disable_selected_tags
          ? undefined
          : (value: Scope | Array<Scope>) => {
              return Array.isArray(value) && value.length > 0
                ? `${value.length} Selected`
                : undefined;
            }
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={
            props.input_icon ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {props.input_icon}
                <BodyText>{props.input_label ?? 'Select Scopes'}</BodyText>
              </Box>
            ) : (
              (props.input_label ?? 'Select Scope')
            )
          }
          variant="standard"
          inputProps={{ ...params.inputProps, readOnly: props.searchable === false }}
          InputProps={{ ...params.InputProps, disableUnderline: props.disable_underline }}
          sx={{
            input: { color: props.color ? props.color : 'currentcolor' },
            '& .MuiSvgIcon-root': {
              color: props.color ? props.color : 'currentcolor'
            }
          }}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<Scope> | Scope | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeSelection(value);
      }}
    />
  );
};

export default ScopeSelect;
